import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, Download, Printer } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Lightbox({ photo, onClose }) {
  const isVideo = photo.type === "video";
  const imgUrl = `${BACKEND_URL}${photo.image_url || photo.thumb_url}`;
  const videoUrl = photo.video_url ? `${BACKEND_URL}${photo.video_url}` : null;
  const downloadUrl = `${BACKEND_URL}${photo.download_url}`;

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const printPhoto = () => {
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Allow pop-ups to print");
      return;
    }
    w.document.write(
      `<html><head><title>${photo.name}</title><style>body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh}img{max-width:100%;max-height:100vh}@media print{img{width:100%}}</style></head><body><img src="${imgUrl}" onload="setTimeout(function(){window.print()},300)"/></body></html>`
    );
    w.document.close();
  };

  const pill =
    "border border-[#F5F1E6]/50 bg-transparent px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase text-[#F5F1E6] hover:bg-[#F5F1E6] hover:text-[#3C3527] transition-colors duration-300 flex items-center gap-2";

  return (
    <motion.div
      data-testid="lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[90] bg-[#241F16]/95 backdrop-blur-md flex items-center justify-center p-6"
      onClick={onClose}
    >
      {isVideo ? (
        <motion.video
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          src={videoUrl}
          poster={`${BACKEND_URL}${photo.thumb_url}`}
          controls
          autoPlay
          playsInline
          data-testid="lightbox-video"
          className="max-h-[85vh] max-w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <motion.img
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          src={imgUrl}
          alt={photo.name}
          data-testid="lightbox-image"
          className="max-h-[85vh] max-w-full object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <div className="absolute top-6 right-6 flex gap-3" onClick={(e) => e.stopPropagation()}>
        <a href={downloadUrl} download data-testid="lightbox-download-btn" className={pill}>
          <Download size={15} /> Download
        </a>
        {!isVideo && (
          <button onClick={printPhoto} data-testid="lightbox-print-btn" className={pill}>
            <Printer size={15} /> Print
          </button>
        )}
        <button onClick={onClose} data-testid="lightbox-close-btn" className={pill} aria-label="Close">
          <X size={15} />
        </button>
      </div>
      <p className="absolute bottom-6 left-6 text-[10px] tracking-[0.2em] uppercase text-[#F5F1E6]/70">
        {photo.name} · {photo.width}×{photo.height}
      </p>
    </motion.div>
  );
}
